import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, ArrowLeft, Shield, Sparkles, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/contexts/LanguageContext";
import { useLogin } from "@/hooks/api/use-auth";
import { useSiteSettings } from "@/hooks/api/use-site-settings";
import RegistrationWizard from "@/components/auth/RegistrationWizard";
import { cn } from "@/lib/utils";
import { getImageUrl } from "@/lib/image-utils";
import { useMemo } from "react";

const Auth = () => {
  const [searchParams] = useSearchParams();
  const isRegister = searchParams.get("mode") === "register";
  const { t, isRTL } = useLanguage();
  
  const loginMutation = useLogin();
  const { data: siteSettings, isLoading: isLoadingSettings } = useSiteSettings();
  
  const logoSrc = useMemo(() => {
    return getImageUrl(siteSettings?.data?.logo);
  }, [siteSettings?.data?.logo]);
  
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  // Show RegistrationWizard for registration mode
  if (isRegister) {
    return <RegistrationWizard />;
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    loginMutation.mutate({
      email: formData.email,
      password: formData.password,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 relative overflow-hidden">
      {/* Subtle Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-72 h-72 bg-primary/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-primary/3 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-primary/2 rounded-full blur-2xl animate-pulse delay-500"></div>
      </div>
      
      {/* Decorative Grid Pattern */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%233b82f6' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}
      />
      
      <div className="relative z-10 min-h-screen flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-md">
          {/* Enhanced Logo and Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-white rounded-2xl shadow-2xl mb-6 group hover:scale-105 transition-transform duration-300 border border-slate-200">
              <Link to="/" className="flex items-center justify-center w-full h-full">
                {isLoadingSettings || !logoSrc ? (
                  <div className="h-16 w-16 animate-pulse bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 bg-[length:200%_100%] rounded-lg" 
                       style={{ animation: 'shimmer 1.5s infinite' }} />
                ) : (
                  <img 
                    src={logoSrc} 
                    alt="KYNZ" 
                    className="h-16 w-auto object-contain"
                  />
                )}
              </Link>
            </div>
            <div className="space-y-4">
              <h1 className="text-4xl font-bold text-slate-900 tracking-tight">
                {t('auth.login') || 'تسجيل الدخول'}
              </h1>
              <p className="text-lg text-slate-600">
                {t('auth.welcomeBackMessage') || 'مرحباً بعودتك'}
              </p>
              <div className="flex items-center justify-center gap-4 mt-6">
                <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-slate-200 shadow-sm">
                  <Shield size={16} className="text-primary" />
                  <span className="text-slate-700 text-sm font-medium">{t('auth.securePlatform')}</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-slate-200 shadow-sm">
                  <Sparkles size={16} className="text-primary" />
                  <span className="text-slate-700 text-sm font-medium">{t('auth.fastEasy')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Form Card */}
          <div className="bg-white border-slate-200 shadow-2xl hover:shadow-3xl transition-all duration-300 rounded-2xl overflow-hidden">
            <div className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    {t('auth.email') || 'البريد الإلكتروني'}
                  </label>
                  <Input
                    type="email"
                    placeholder={t('auth.emailPlaceholder') || 'أدخل بريدك الإلكتروني'}
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    disabled={loginMutation.isPending}
                    className="bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-primary focus:ring-primary transition-all duration-300"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    {t('auth.password') || 'كلمة المرور'}
                  </label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder={t('auth.passwordPlaceholder') || 'أدخل كلمة المرور'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      disabled={loginMutation.isPending}
                      className={cn(
                        "bg-white border-slate-200 text-slate-900 placeholder-slate-400 focus:border-primary focus:ring-primary transition-all duration-300",
                        isRTL ? "pr-10" : "pl-10"
                      )}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors`}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                
                <div className={isRTL ? "text-left" : "text-right"}>
                  <Link to="/forgot-password" className="text-sm text-primary hover:underline underline-offset-2 transition-all duration-300">
                    {t('auth.forgotPassword') || 'نسيت كلمة المرور؟'}
                  </Link>
                </div>

                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full bg-primary text-white hover:bg-primary/90 transition-all duration-300 shadow-lg hover:shadow-xl"
                  disabled={loginMutation.isPending}
                >
                  {loginMutation.isPending ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                      {t('common.loading') || 'جاري المعالجة...'}
                    </>
                  ) : (
                    <>
                      <Shield size={16} className="mr-2" />
                      {t('auth.login') || 'تسجيل الدخول'}
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Enhanced Footer */}
            <div className="px-8 pb-8 space-y-4">
              <div className="text-center">
                <p className="text-slate-600">
                  {t('auth.noAccount') || 'ليس لديك حساب؟'}{' '}
                  <Link 
                    to="/auth?mode=register" 
                    className="text-primary font-semibold hover:underline underline-offset-2 transition-all duration-300"
                  >
                    {t('auth.registerNow') || 'إنشاء حساب جديد'}
                  </Link>
                </p>
              </div>
              
              <div className="text-center">
                <Link to="/" className="text-slate-500 hover:text-primary inline-flex items-center gap-2 transition-colors duration-300">
                  <ArrowLeft size={16} />
                  {t('auth.backToHome') || 'العودة للرئيسية'}
                </Link>
              </div>

              {/* Additional Features */}
              <div className="pt-4 border-t border-slate-200">
                <div className="flex items-center justify-center gap-6 text-xs text-slate-500">
                  <div className="flex items-center gap-1">
                    <Shield size={12} className="text-slate-400" />
                    <span>{t('auth.secureConnection')}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Award size={12} className="text-slate-400" />
                    <span>{t('auth.dataProtection')}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Sparkles size={12} className="text-slate-400" />
                    <span>{t('auth.support247')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
